// src/components/WeighInModal/WeighInModal.js
import React, { useMemo, useState } from "react";
import "./WeighInModal.css";
import apiClient from "../../api/apiClient";
import { formatWeightFromGrams } from "../../utils/weightUtils";
import { partitionWeighInProducts, searchOutOfStock, weighInColor, weighInDisplayName } from "./weighInProducts";
import ColorDot from "../ColorDot/ColorDot";
import { useConfirm } from "../../context/confirmContext";
import { useEscapeKey } from "../ui/useEscapeKey";

const WeighInModal = ({ products, licenseNo, locationId, onClose, onSuccess }) => {
    const confirm = useConfirm();
    useEscapeKey(onClose);
    const [activeTab, setActiveTab] = useState("weigh-in");
    const [inputs, setInputs] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitResult, setSubmitResult] = useState(null); 
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    // Ids of out-of-stock products the user pulled into this weigh-in via search
    const [addedIds, setAddedIds] = useState(() => new Set());

    const { inStock, outOfStock } = useMemo(() => partitionWeighInProducts(products), [products]);
    const addedProducts = outOfStock.filter((p) => addedIds.has(p.id));
    const weighableProducts = [...inStock, ...addedProducts];
    const searchResults = searchOutOfStock(outOfStock, searchTerm, addedIds);

    const handleAddProduct = (productId) => {
        setAddedIds((prev) => new Set(prev).add(productId));
        setSearchTerm("");
    };

    const handleRemoveProduct = (productId) => {
        setAddedIds((prev) => {
            const next = new Set(prev);
            next.delete(productId);
            return next;
        });
        setInputs((prev) => {
            const next = { ...prev };
            delete next[productId];
            return next;
        });
    };

    const fetchHistory = async () => {
        setHistoryLoading(true);
        setHistoryError("");
        try {
            const locationParam = locationId ? `&locationId=${locationId}` : "";
            const res = await apiClient.get(`/weigh-in/history?licenseNo=${licenseNo}${locationParam}`);
            setHistory(Array.isArray(res.data) ? res.data : []);
        } catch {
            setHistoryError("Could not load weigh-in history.");
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === "history" && history.length === 0 && !historyLoading) {
            fetchHistory();
        }
    };

    const handleInputChange = (productId, value) => {
        setInputs((prev) => ({ ...prev, [productId]: value }));
    };

    const handleSubmit = async () => {
        const listedIds = new Set(weighableProducts.map((p) => String(p.id)));
        const entries = Object.entries(inputs)
            .filter(([productId, val]) => listedIds.has(productId) && val.trim() !== "")
            .map(([productId, val]) => ({
                productId: parseInt(productId, 10),
                quantityEntered: parseFloat(val),
            }))
            .filter((e) => !isNaN(e.quantityEntered) && e.quantityEntered >= 0);

        if (entries.length === 0) {
            setSubmitError("Enter at least one weight to submit.");
            return;
        }

        const ok = await confirm({
            title: "Submit Weigh-In",
            message: `Submit weigh-in for ${entries.length} product${entries.length !== 1 ? "s" : ""}? This will update current inventory weights.`,
            confirmLabel: "Submit",
            variant: "warning",
        });
        if (!ok) return;

        setSubmitting(true);
        setSubmitError("");
        try {
            const res = await apiClient.post("/weigh-in", {
                licenseNo,
                locationId: locationId || null,
                entries,
            });
            setSubmitResult(res.data);
            // Force the History tab to refetch so it includes this submission
            setHistory([]);
            if (typeof onSuccess === "function") onSuccess();
        } catch (err) {
            setSubmitError(err.response?.data?.error || "Weigh-in failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        setInputs({});
        setAddedIds(new Set());
        setSearchTerm("");
        setSubmitResult(null);
        setSubmitError("");
    };

    const formatQty = (qty, unit) => {
        const value = parseFloat(qty) || 0;
        if (unit === "units") return `${value} units`;
        return formatWeightFromGrams(value);
    };

    // Fixed-width slot so product names stay aligned whether or not a row has a color
    const renderColorDot = (product) => {
        const color = weighInColor(product);
        if (!color) return <span className="wim__dot-slot" />;
        return (
            <span className="wim__dot-slot" role="img" aria-label={`${color} color`} title={color}>
                <ColorDot color={color} className="wim__dot" />
            </span>
        );
    };

    const productNameById = (productId) => {
        const match = (products || []).find((p) => p.id === productId);
        return match ? weighInDisplayName(match) : `Product #${productId}`;
    };

    const formatDate = (ts) => {
        const d = new Date(ts);
        return d.toLocaleString("en-US", {
            month: "short", day: "numeric", year: "numeric",
            hour: "numeric", minute: "2-digit",
        });
    };

    return (
        <div className="wim-backdrop" onClick={onClose} role="dialog" aria-modal="true">
            <div className="wim" onClick={(e) => e.stopPropagation()}>
                <div className="wim__header">
                    <h3 className="wim__title">Weigh-In</h3>
                    <button className="wim__close" onClick={onClose} aria-label="Close">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="wim__tabs">
                    <button
                        className={`wim__tab${activeTab === "weigh-in" ? " wim__tab--active" : ""}`}
                        onClick={() => handleTabChange("weigh-in")}
                    >
                        Weigh-In
                    </button>
                    <button
                        className={`wim__tab${activeTab === "history" ? " wim__tab--active" : ""}`}
                        onClick={() => handleTabChange("history")}
                    >
                        History
                    </button>
                </div>

                {activeTab === "weigh-in" && (
                    <>
                        {!submitResult && (
                            <div className="wim__search">
                                <input
                                    className="wim__search-input"
                                    type="search"
                                    placeholder="Search out-of-stock products to add…"
                                    aria-label="Search out-of-stock products to add"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm.trim() !== "" && (
                                    <ul className="wim__search-results">
                                        {searchResults.length === 0 ? (
                                            <li className="wim__search-empty">No out-of-stock products match.</li>
                                        ) : (
                                            searchResults.map((p) => (
                                                <li key={p.id} className="wim__search-result">
                                                    <span className="wim__search-name">
                                                        {renderColorDot(p)}
                                                        {weighInDisplayName(p)}
                                                        {p.sku && <span className="wim__search-sku">{p.sku}</span>}
                                                    </span>
                                                    <button
                                                        className="wim__search-add"
                                                        onClick={() => handleAddProduct(p.id)}
                                                    >
                                                        Add
                                                    </button>
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                        <div className="wim__body" data-tutorial="wim-body">
                            {submitResult ? (
                                <div className="wim__result">
                                    <p className="wim__result-success">
                                        {submitResult.updated.length} product{submitResult.updated.length !== 1 ? "s" : ""} updated.
                                    </p>
                                    {submitResult.skipped.length > 0 && (
                                        <div className="wim__result-skipped">
                                            <p className="wim__result-skipped-label">
                                                {submitResult.skipped.length} could not be updated:
                                            </p>
                                            <ul className="wim__result-skipped-list">
                                                {submitResult.skipped.map((s) => (
                                                    <li key={s.productId}>{productNameById(s.productId)}: {s.error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ) : weighableProducts.length === 0 ? (
                                <p className="wim__empty">No products with weight on hand. Search above to add an out-of-stock product.</p>
                            ) : (
                                <table className="wim__table">
                                    <thead>
                                        <tr>
                                            <th className="wim__th">Product</th>
                                            <th className="wim__th wim__th--current">Current</th>
                                            <th className="wim__th wim__th--input">New Weight</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {weighableProducts.map((p) => (
                                            <tr key={p.id} className="wim__row">
                                                <td className="wim__td wim__td--name">
                                                    {renderColorDot(p)}
                                                    {weighInDisplayName(p)}
                                                    {addedIds.has(p.id) && (
                                                        <button
                                                            className="wim__remove"
                                                            onClick={() => handleRemoveProduct(p.id)}
                                                            aria-label={`Remove ${weighInDisplayName(p)} from weigh-in`}
                                                            title="Remove from this weigh-in"
                                                        >
                                                            Added ×
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="wim__td wim__td--current">
                                                    {formatQty(p.quantity, p.weight_unit)}
                                                </td>
                                                <td className="wim__td wim__td--input">
                                                    <input
                                                        className="wim__input"
                                                        type="number"
                                                        min="0"
                                                        step={p.weight_unit === "units" ? "1" : "any"}
                                                        placeholder={p.weight_unit === "units" ? "count" : "grams"}
                                                        value={inputs[p.id] ?? ""}
                                                        onChange={(e) => handleInputChange(p.id, e.target.value)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="wim__footer">
                            {submitError && <p className="wim__error">{submitError}</p>}
                            {submitResult ? (
                                <>
                                    <button className="wim__btn wim__btn--secondary" onClick={handleReset}>
                                        Weigh In Again
                                    </button>
                                    <button className="wim__btn wim__btn--primary" onClick={onClose}>
                                        Done
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button className="wim__btn wim__btn--secondary" onClick={onClose}>
                                        Cancel
                                    </button>
                                    <button
                                        className="wim__btn wim__btn--primary"
                                        onClick={handleSubmit}
                                        disabled={submitting || weighableProducts.length === 0}
                                    >
                                        {submitting ? "Submitting…" : "Submit Weigh-In"}
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                )}

                {activeTab === "history" && (
                    <div className="wim__body wim__body--history">
                        {historyLoading && <p className="wim__loading">Loading history…</p>}
                        {historyError && <p className="wim__error">{historyError}</p>}
                        {!historyLoading && !historyError && history.length === 0 && (
                            <p className="wim__empty">No weigh-in history yet.</p>
                        )}
                        {!historyLoading && history.length > 0 && (
                            <>
                                <div className="wim__history-refresh">
                                    <button className="wim__refresh-btn" onClick={fetchHistory}>↺ Refresh</button>
                                </div>
                                <table className="wim__table wim__table--history">
                                    <thead>
                                        <tr>
                                            <th className="wim__th">Date / Time</th>
                                            <th className="wim__th">Product</th>
                                            <th className="wim__th">Previous</th>
                                            <th className="wim__th">New Weight</th>
                                            <th className="wim__th">Staff</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((row) => (
                                            <tr key={row.id} className="wim__row">
                                                <td className="wim__td wim__td--date">{formatDate(row.created_at)}</td>
                                                <td className="wim__td">{row.product_name}</td>
                                                <td className="wim__td">{formatQty(row.quantity_before, row.weight_unit)}</td>
                                                <td className="wim__td">{formatQty(row.quantity_entered, row.weight_unit)}</td>
                                                <td className="wim__td wim__td--staff">
                                                    {row.firstName
                                                        ? `${row.firstName} ${row.lastName || ""}`.trim()
                                                        : row.weighed_by.slice(0, 8)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WeighInModal;
